/** Keep the widget in the browser's top layer, including inside modal dialogs. */
export function watchWidgetLayer(host: HTMLElement): () => void {
  // Older browsers retain the maximum-z-index stylesheet fallback.
  if (typeof host.showPopover !== "function") return () => {};

  host.popover = "manual";
  host.style.cssText =
    "position:fixed!important;inset:0 auto auto 0!important;width:0!important;height:0!important;margin:0!important;padding:0!important;border:0!important;overflow:visible!important;background:transparent!important;";
  let disposed = false;
  let modals: Element[] = [];

  const promote = (): void => {
    if (disposed) return;
    const open = [...document.querySelectorAll(":modal")];
    modals = [
      ...modals.filter((modal) => open.includes(modal)),
      ...open.filter((modal) => !modals.includes(modal)),
    ];
    // A popover outside a modal is still inert. It must also be its descendant.
    const parent = modals.at(-1) ?? document.fullscreenElement ?? document.body;
    const focused = host.shadowRoot?.activeElement;
    if (host.matches(":popover-open")) host.hidePopover();
    if (host.parentElement !== parent) parent.appendChild(host);
    host.showPopover();
    if (focused instanceof HTMLElement) focused.focus({ preventScroll: true });
  };

  const observer = new MutationObserver((records) => {
    const modalChanged = records.some(
      (record) => record.type === "attributes" && record.target instanceof HTMLDialogElement,
    );
    if (modalChanged || !host.isConnected || modals.some((modal) => !modal.isConnected)) promote();
  });
  const onToggle = (event: Event): void => {
    if (event.target === host || !(event instanceof ToggleEvent) || event.newState !== "open")
      return;
    // Declarative popovertarget actions run microtasks while showPopover is still
    // active. Wait for the next task before hiding and re-showing our popover.
    const target = event.target;
    setTimeout(() => {
      if (disposed) return;
      if (target instanceof HTMLDialogElement && target.matches(":modal")) {
        modals = [...modals.filter((modal) => modal !== target), target];
      }
      promote();
    });
  };
  document.addEventListener("beforetoggle", onToggle, true);
  document.addEventListener("fullscreenchange", promote);
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["open"],
  });
  promote();
  return () => {
    disposed = true;
    observer.disconnect();
    document.removeEventListener("beforetoggle", onToggle, true);
    document.removeEventListener("fullscreenchange", promote);
  };
}
